import { FieldArray } from 'formik';
import { ProgramAttachment } from '../../types';
import { AttachmentsTable } from './AttachmentsTable';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useAuth from '../../../auth/authContext';
import { PleaseRelogin } from '../../../auth/PleaseRelogin';
import { useAttachmentDownloadMutation } from '../../hooks/useAttachmentDownloadMutation';
import { analyticsTrack } from '../../../web-analytics/webAnalytics';
import { AnalyticsEvent } from '../../../web-analytics/AnalyticsEvent';
import { deleteAttachment } from './util';
import { AddAttachment } from './AddAttachment';
import { PaperclipIcon } from '../../../assets/icons';
import { AttachmentsModal } from './AttachmentsModal';

export const Attachments = (props: {
  attachments: ProgramAttachment[];
  programId: string;
  hasAddAccess: boolean;
  hasDeleteAccess: boolean;
}) => {
  const { attachments, programId, hasAddAccess, hasDeleteAccess } = props;
  const nonDeletedAttachments = useMemo(
    () => attachments.filter((attachment) => attachment.status !== 'DELETED'),
    [attachments]
  );

  const { mutate: download } = useAttachmentDownloadMutation();

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  useEffect(() => {
    if (selectedIndex !== null) {
      if (nonDeletedAttachments.length === 0) setSelectedIndex(null);
      else setSelectedIndex(Math.min(nonDeletedAttachments.length - 1, selectedIndex));
    }
  }, [nonDeletedAttachments, selectedIndex]);

  const preview = (attachment: ProgramAttachment) => {
    let index: number | null = nonDeletedAttachments.findIndex((x) => x.id === attachment.id);
    if (index < 0) index = null;
    setSelectedIndex(index);
  };
  const closeAttachmentView = useCallback(() => {
    setSelectedIndex(null);
  }, []);

  const {
    state: { user },
  } = useAuth();
  if (!user) return <PleaseRelogin />;

  return (
    <div style={{ marginLeft: '-12px', marginRight: '-12px' }}>
      <FieldArray name="attachments">
        {(arrayHelpers) => (
          <>
            <AttachmentsTable
              values={nonDeletedAttachments}
              actions={{
                download: (a) => {
                  analyticsTrack(AnalyticsEvent.PROGRAM_ATTACHMENT_DOWNLOADED, {
                    button: 'menu',
                  });
                  download({ programId, attachment: a });
                },
                preview: (a) => preview(a),
                delete: (a) => deleteAttachment(attachments, arrayHelpers, a),
              }}
              id={programId}
              hasDeleteAccess={hasDeleteAccess}
            />

            {hasAddAccess && (
              <AddAttachment
                maxAttachments={10}
                maxSizeMb={5}
                icon={<PaperclipIcon />}
                text="Attach new"
                buttonClassName="attachments-table__add-btn"
                dataTest="program-edit__attachment-add"
                notDeletedAttachmentsCount={
                  attachments.filter((attachment) => attachment.status !== 'DELETED').length
                }
                arrayHelpers={arrayHelpers}
              />
            )}
            <AttachmentsModal
              programId={programId}
              nonDeletedAttachments={nonDeletedAttachments}
              selectedIndex={selectedIndex}
              onClose={closeAttachmentView}
              onDelete={(attachment) => deleteAttachment(attachments, arrayHelpers, attachment)}
            />
          </>
        )}
      </FieldArray>
    </div>
  );
};
